import React, { useMemo, useCallback, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, X, Clock, Calendar, Users, Search } from 'lucide-react';
import { DayTemplateShift, Employee, DayShift } from '@/types';
import { cn } from '@/lib/core/utils';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useRoles } from '@/hooks/employees/useRoles';
import DraggableEmployeeCard from '@/components/shifts/DraggableEmployeeCard';

interface TemplateShiftActionsPanelProps {
  shifts: DayTemplateShift[];
  selectedShiftIds: Set<string>;
  onShiftSelect: (shiftId: string, selected: boolean) => void;
  onClearSelection: () => void;
  onDeleteSelected: () => void;
  employees?: Employee[];
  businessId?: string | null;
  selectedDate?: Date;
  allDayShifts?: DayShift[];
  onEmployeeDragStart?: (employee: Employee) => void;
  onEmployeeDragEnd?: () => void;
  saveEmployeeAssignments?: boolean;
  className?: string;
}

const TemplateShiftActionsPanel: React.FC<TemplateShiftActionsPanelProps> = ({
  shifts,
  selectedShiftIds,
  onShiftSelect,
  onClearSelection,
  onDeleteSelected,
  employees = [],
  businessId,
  selectedDate,
  allDayShifts = [],
  onEmployeeDragStart,
  onEmployeeDragEnd,
  saveEmployeeAssignments = false,
  className = "",
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'shifts' | 'employees'>('shifts');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [isRoleFilterOpen, setIsRoleFilterOpen] = useState(false);

  // Fetch roles for the business
  const { data: roles = [] } = useRoles(businessId || '');

  // Get selected template shifts
  const selectedShifts = useMemo(() => {
    return shifts.filter((_, index) => {
      const shiftId = `template-shift-${index}`;
      return selectedShiftIds.has(shiftId);
    }).map((shift, originalIndex) => ({
      ...shift,
      index: shifts.findIndex((s, i) => {
        const shiftId = `template-shift-${i}`;
        return selectedShiftIds.has(shiftId) && s === shift;
      })
    }));
  }, [shifts, selectedShiftIds]);

  // Handle delete confirmation
  const handleDeleteClick = useCallback(() => {
    if (selectedShiftIds.size === 0) return;
    setShowDeleteDialog(true);
  }, [selectedShiftIds]);

  const handleConfirmDelete = useCallback(() => {
    onDeleteSelected();
    setShowDeleteDialog(false);
  }, [onDeleteSelected]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  // Handle role selection toggle
  const toggleRoleSelection = useCallback((roleId: string) => {
    setSelectedRoleIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleId)) {
        newSet.delete(roleId);
      } else {
        newSet.add(roleId);
      }
      return newSet;
    });
  }, []);

  // Clear all role filters
  const clearRoleFilters = useCallback(() => {
    setSelectedRoleIds(new Set());
  }, []);

  // Filter employees based on search and role filters
  const filteredEmployees = useMemo(() => {
    let filtered = employees;

    // Filter by roles if any are selected
    if (selectedRoleIds.size > 0) {
      filtered = filtered.filter(employee =>
        employee.role_id && selectedRoleIds.has(employee.role_id)
      );
    }

    // Filter by search term
    const term = searchTerm.toLowerCase().trim();
    if (term) {
      filtered = filtered.filter(employee =>
        employee.name.toLowerCase().includes(term) ||
        (employee.roleInfo?.name || '').toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [employees, selectedRoleIds, searchTerm]);

  return (
    <div className={`h-full flex flex-col bg-background border-l dark:border-gray-600 ${className}`}>
      {/* Header */}
      <div className="p-2 space-y-2 flex-shrink-0 border-b dark:border-gray-600">
        {/* Mode selector */}
        <Select value={selectedMode} onValueChange={(value) => setSelectedMode(value as 'shifts' | 'employees')}>
          <SelectTrigger className="h-7 text-xs !bg-background w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="shifts">
              <div className="flex items-center gap-1 text-xs">
                Shifts
              </div>
            </SelectItem>
            <SelectItem value="employees">
              <div className="flex items-center gap-1 text-xs">
                Employees
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Shifts mode: Selection info and actions */}
        {selectedMode === 'shifts' && selectedShiftIds.size > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <Badge className="text-xs px-1 py-0">
                {selectedShiftIds.size} selected
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0"
                onClick={onClearSelection}
                title="Clear selection"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>

            {/* Bulk actions */}
            <div className="flex gap-1 justify-center">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                onClick={handleDeleteClick}
                title="Delete selected shifts"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Employees mode: Search and filters */}
        {selectedMode === 'employees' && (
          <>
            <div className="relative">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-6 text-xs !bg-background w-full"
                placeholder="Search..."
              />
              <Search className="h-3 w-3 absolute right-2 top-1.5 text-muted-foreground" />
            </div>

            <Popover open={isRoleFilterOpen} onOpenChange={setIsRoleFilterOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={
                    selectedRoleIds.size > 0
                      ? 'w-full h-8 justify-between border-2 border-primary dark:border-primary'
                      : 'w-full h-8 justify-around'
                  }
                >
                  <span className="flex items-center truncate" style={{gap: '0.25rem'}}>
                    <Users className="h-3 w-3 flex-shrink-0" />
                    {(() => {
                      if (selectedRoleIds.size === 0) {
                        return 'Roles';
                      }
                      if (selectedRoleIds.size === 1) {
                        const selectedRole = roles.find(r => selectedRoleIds.has(r.id));
                        return <span className="truncate text-xs">{selectedRole?.name || 'Role'}</span>;
                      }
                      return <span className="truncate text-xs">{selectedRoleIds.size} roles</span>;
                    })()}
                  </span>
                  {selectedRoleIds.size > 0 && (
                    <div
                      className="relative z-10 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-800/30 rounded p-1 flex-shrink-0 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearRoleFilters();
                      }}
                    >
                      <X className="h-4 w-4 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                    </div>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" align="start" className="w-auto p-0" sideOffset={4}>
                <div className="p-3">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Roles
                        {selectedRoleIds.size > 0 && (
                          <span className="bg-indigo-100 text-indigo-800 text-xs px-1.5 py-0.5 rounded-full">
                            {selectedRoleIds.size}
                          </span>
                        )}
                      </h4>
                    </div>

                    <div className="flex justify-center" style={{gap: '0.25rem'}}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs flex-1"
                        onClick={() => {
                          const allRoleIds = new Set(roles.map(r => r.id));
                          setSelectedRoleIds(allRoleIds);
                        }}
                      >
                        All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs flex-1"
                        onClick={clearRoleFilters}
                      >
                        None
                      </Button>
                    </div>

                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {roles.map(role => {
                        const employeeCount = employees.filter(e => e.role_id === role.id).length;

                        return (
                          <div key={role.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`role-${role.id}`}
                              checked={selectedRoleIds.has(role.id)}
                              onCheckedChange={() => toggleRoleSelection(role.id)}
                            />
                            <label
                              htmlFor={`role-${role.id}`}
                              className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2 flex-1"
                            >
                              <span className="truncate">{role.name} ({employeeCount})</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>

      {/* Scrollable content */}
      <ScrollArea className="flex-1 px-2">
        {selectedMode === 'shifts' && selectedShiftIds.size === 0 ? (
          <div className="text-xs text-muted-foreground py-8 text-center">
            <Calendar className="h-4 w-4 mx-auto mb-2 opacity-50" />
            <p className="mb-1">No shifts selected</p>
            <p className="text-xxs opacity-75">Click on shifts in the grid to select them</p>
          </div>
        ) : selectedMode === 'shifts' ? (
          <div className="space-y-1 pb-2 mt-2">
            {selectedShifts.map((shift, displayIndex) => {
              const shiftId = `template-shift-${shift.index}`;
              const isSelected = selectedShiftIds.has(shiftId);

              return (
                <div
                  key={shiftId}
                  className={cn(
                    "p-2 rounded border text-xs cursor-pointer hover:bg-muted transition-colors",
                    isSelected ? "bg-secondary/20 border-secondary" : "bg-background border-border"
                  )}
                  onClick={() => onShiftSelect(shiftId, !isSelected)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium truncate">{shift.title || 'Shift'}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-4 w-4 p-0 text-red-600 hover:text-red-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Single shift delete: just select it and delete
                        onShiftSelect(shiftId, true);
                        setTimeout(() => handleDeleteClick(), 0);
                      }}
                      title="Delete this shift"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>{shift.startTime} - {shift.endTime}</span>
                  </div>
                  {shift.employeeRole && (
                    <div className="text-muted-foreground mt-1">
                      Role: {shift.employeeRole}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Employees mode */
          !saveEmployeeAssignments ? (
            <div className="text-xs text-muted-foreground py-8 text-center px-2">
              <Users className="h-4 w-4 mx-auto mb-2 opacity-50" />
              <p className="mb-1">Employee mode unavailable</p>
              <p className="text-xxs opacity-75">Enable "Save employee assignments" to use this mode</p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-xs text-muted-foreground py-8 text-center">
              <Users className="h-4 w-4 mx-auto mb-2 opacity-50" />
              <p className="mb-1">No employees found</p>
              <p className="text-xxs opacity-75">
                {employees.length === 0 ? 'No employees available' : 'No employees match your filters'}
              </p>
            </div>
          ) : (
            <div className="space-y-2 p-2">
              {filteredEmployees.map(employee => (
                <DraggableEmployeeCard
                  key={employee.id}
                  employee={employee}
                  selectedDate={selectedDate || new Date()}
                  allDayShifts={allDayShifts}
                  onDragStart={onEmployeeDragStart}
                  onDragEnd={onEmployeeDragEnd}
                />
              ))}
            </div>
          )
        )}
      </ScrollArea>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
        if (!open) handleCancelDelete();
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete Shift(s) from Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedShiftIds.size} shift(s) from this template?
              This will not affect any scheduled shifts, only this template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete from Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TemplateShiftActionsPanel;
